import React, { useEffect, useMemo } from 'react'
import { enUS, ptBR } from "@mui/material/locale";
import { connect } from "react-redux";
import { createTheme, ThemeProvider, CssBaseline } from "@mui/material";
import usePageViews from "../../packages/useGTM/usePageViews";
import { useLocation, useNavigate } from "react-router-dom";
import { ThemeMode } from "../../features/Theme/themeReducer";

const getTheme = (mode: ThemeMode) => {
    const isDark = mode === 'dark';

    return createTheme({
        palette: {
            mode: mode,
            primary: {
                // Muted, softer purple for dark mode, muted blue for light mode
                main: isDark ? '#6B5B8E' : '#5A7FA8',
                light: isDark ? '#8A7AA8' : '#7A9FC8',
                dark: isDark ? '#4D3D6E' : '#3D5F88',
                contrastText: '#ffffff',
            },
            secondary: {
                // Muted teal/accent color - softer and less bright
                main: isDark ? '#5A9A94' : '#4A8A8A',
                light: isDark ? '#7ABAA4' : '#6AAAAA',
                dark: isDark ? '#3A7A74' : '#2A6A6A',
                contrastText: '#ffffff',
            },
            background: {
                // Rich dark backgrounds for dark mode, clean whites for light mode
                default: isDark ? '#0F1419' : '#FAFBFC',
                paper: isDark ? '#1A1F2E' : '#FFFFFF',
            },
            text: {
                primary: isDark ? '#E8EAED' : '#1A1F2E',
                secondary: isDark ? '#9AA0A6' : '#5F6368',
            },
            divider: isDark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.08)',
            // Custom colors - toned down for comfort
            error: {
                main: isDark ? '#D95555' : '#C62828', // Softer, muted red for LIVE indicator
            },
            success: {
                main: isDark ? '#4A9E5A' : '#388E3C', // Muted green for accents
            },
            warning: {
                main: isDark ? '#D8955A' : '#E67E22', // Muted orange/warning
            },
            info: {
                main: isDark ? '#5A9FC8' : '#4A7FA8', // Muted blue for info
            },
        },
        components: {
            MuiButton: {
                styleOverrides: {
                    root: {
                        borderRadius: 8,
                        textTransform: 'none',
                        fontWeight: 500,
                        padding: '10px 24px',
                        boxShadow: 'none',
                        '&:hover': {
                            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
                        },
                    },
                    contained: {
                        '&:hover': {
                            boxShadow: isDark
                                ? '0 4px 12px rgba(107, 91, 142, 0.3)'
                                : '0 4px 12px rgba(90, 127, 168, 0.25)',
                        },
                    },
                    containedPrimary: {
                        backgroundColor: isDark ? '#6B5B8E' : '#5A7FA8',
                        color: '#ffffff',
                        '&:hover': {
                            backgroundColor: isDark ? '#8A7AA8' : '#3D5F88',
                        },
                        '&:disabled': {
                            backgroundColor: isDark ? 'rgba(107, 91, 142, 0.3)' : 'rgba(90, 127, 168, 0.3)',
                            color: isDark ? 'rgba(255, 255, 255, 0.5)' : 'rgba(255, 255, 255, 0.7)',
                        },
                    },
                    containedSecondary: {
                        backgroundColor: isDark ? '#5A9A94' : '#4A8A8A',
                        color: '#ffffff',
                        '&:hover': {
                            backgroundColor: isDark ? '#7ABAA4' : '#2A6A6A',
                        },
                        '&:disabled': {
                            backgroundColor: isDark ? 'rgba(90, 154, 148, 0.3)' : 'rgba(74, 138, 138, 0.3)',
                            color: isDark ? 'rgba(255, 255, 255, 0.5)' : 'rgba(255, 255, 255, 0.7)',
                        },
                    },
                },
            },
            MuiInputLabel: {
                styleOverrides: {
                    root: {
                        "&.Mui-focused": {
                            color: isDark ? '#6B5B8E' : '#5A7FA8',
                        }
                    }
                }
            },
            MuiCssBaseline: {
                styleOverrides: {
                    body: {
                        backgroundColor: isDark ? '#0F1419' : '#FAFBFC',
                        color: isDark ? '#E8EAED' : '#1A1F2E',
                        transition: 'background-color 0.3s ease, color 0.3s ease',
                    }
                }
            },
        }
    }, ptBR);
};

function Layout(props) {
    const {
        children,
        isLoggedIn,
        profile,
        themeMode,
    } = props;

    const navigate = useNavigate();

    const location = useLocation();

    usePageViews(profile?.code);

    // Create theme based on mode
    const theme = useMemo(() => getTheme(themeMode || 'dark'), [themeMode]);

    useEffect(() => {
        console.debug('---------------', location.pathname, '---------------');
        if (!isLoggedIn &&
            ['/', '/login', '/signup', '/widget/comments/user', '/login-magic-link', '/close', '/onboarding'].includes(location.pathname) === false &&
            !location.pathname.startsWith('/widget/comments/user')) {
            navigate('/');
        }
    }, [isLoggedIn]);

    return (
        <ThemeProvider theme={theme}>
            <CssBaseline />
            {children}
        </ThemeProvider>
    );
}

const mapStateToProps = function (state) {
    return {
        isLoggedIn: state.auth.isLoggedIn,
        profile: state.user.profile,
        themeMode: state.theme?.mode || 'dark',
    };
};
export default connect(mapStateToProps)(Layout)
